import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface OnboardingState {
  hasCompletedOnboarding: boolean;
  showOnboarding: boolean;
  lastCompletedStep: number;
  completedAt?: string;
}

interface OnboardingContextType extends OnboardingState {
  startOnboarding: () => void;
  completeOnboarding: () => void;
  hideOnboarding: () => void;
  resetOnboardingState: () => void;
  updateLastCompletedStep: (step: number) => void;
  shouldShowOnboarding: () => boolean;
  showOnboardingForReturningUser: () => void;
  clearOnboardingData: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const ONBOARDING_STORAGE_KEY = 'jarvis_onboarding_state';

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const [onboardingState, setOnboardingState] = useState<OnboardingState>({
    hasCompletedOnboarding: false,
    showOnboarding: false,
    lastCompletedStep: -1,
  });

  // Load onboarding state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        setOnboardingState(parsedState);
      } catch (error) {
        console.error('Error parsing onboarding state from localStorage:', error);
        // Reset to default state if parsing fails
        resetOnboardingState();
      }
    } else {
      // First-time user - show onboarding
      setOnboardingState(prev => ({
        ...prev,
        showOnboarding: true,
      }));
    }
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(onboardingState));
  }, [onboardingState]);

  const startOnboarding = () => {
    setOnboardingState(prev => ({
      ...prev,
      showOnboarding: true,
      lastCompletedStep: -1,
    }));
  };

  const completeOnboarding = () => {
    setOnboardingState(prev => ({
      ...prev,
      hasCompletedOnboarding: true,
      showOnboarding: false,
      completedAt: new Date().toISOString(),
    }));
  };

  const hideOnboarding = () => {
    setOnboardingState(prev => ({
      ...prev,
      showOnboarding: false,
    }));
  };

  const resetOnboardingState = () => {
    const defaultState: OnboardingState = {
      hasCompletedOnboarding: false,
      showOnboarding: true,
      lastCompletedStep: -1,
    };
    setOnboardingState(defaultState);
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(defaultState));
  };

  const updateLastCompletedStep = (step: number) => {
    setOnboardingState(prev => ({
      ...prev,
      lastCompletedStep: Math.max(prev.lastCompletedStep, step),
    }));
  };

  const shouldShowOnboarding = () => {
    return onboardingState.showOnboarding;
  };

  const showOnboardingForReturningUser = () => {
    // First set to false, then to true to force a state change
    setOnboardingState(prev => ({
      ...prev,
      showOnboarding: false,
    }));
    
    // Use setTimeout to ensure the state change is processed
    setTimeout(() => {
      setOnboardingState(prev => ({
        ...prev,
        showOnboarding: true,
      }));
    }, 10);
  };

  const clearOnboardingData = () => {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    setOnboardingState({
      hasCompletedOnboarding: false,
      showOnboarding: true,
      lastCompletedStep: -1,
    });
  };

  const value: OnboardingContextType = {
    ...onboardingState,
    startOnboarding,
    completeOnboarding,
    hideOnboarding,
    resetOnboardingState,
    updateLastCompletedStep,
    shouldShowOnboarding,
    showOnboardingForReturningUser,
    clearOnboardingData,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export default useOnboarding;